import styled from "styled-components";
import { theme } from "../theme";

// Text Message Styles
export const TextMessageContainer = styled.div<{ $isOwn: boolean }>`
  max-width: 70%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.borderRadius.lg};
  background: ${(props) =>
    props.$isOwn
      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      : theme.colors.background.secondary};
  color: ${(props) =>
    props.$isOwn ? theme.colors.text.light : theme.colors.text.primary};
  box-shadow: ${theme.shadows.light};
  position: relative;
  
  &:hover {
    box-shadow: ${theme.shadows.medium};
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    max-width: 85%;
    padding: ${theme.spacing.md};
    border-radius: ${theme.borderRadius.xl};
  }
`;

export const TextContent = styled.div`
  word-wrap: break-word;
  line-height: 1.4;
  white-space: pre-wrap;
`;

export const TextActions = styled.div`
  position: absolute;
  top: -8px;
  right: ${theme.spacing.sm};
  display: flex;
  gap: ${theme.spacing.xs};
  opacity: 0;
  transition: opacity ${theme.transitions.normal};
  
  ${TextMessageContainer}:hover & {
    opacity: 1;
  }
`;

export const TextActionButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  box-shadow: ${theme.shadows.light};
  transition: all ${theme.transitions.normal};
  
  &:hover {
    background: ${theme.colors.primary};
    color: ${theme.colors.text.light};
  }
`;

// Image Message Styles
export const ImageMessageContainer = styled.div<{ $isOwn: boolean }>`
  max-width: 70%;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.lg};
  background: ${(props) =>
    props.$isOwn
      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      : theme.colors.background.secondary};
  color: ${(props) =>
    props.$isOwn ? theme.colors.text.light : theme.colors.text.primary};
  box-shadow: ${theme.shadows.light};
  position: relative;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    max-width: 85%;
    padding: ${theme.spacing.md};
    border-radius: ${theme.borderRadius.xl};
  }
`;

export const ImagePreview = styled.div`
  position: relative;
  cursor: pointer;
  border-radius: ${theme.borderRadius.md};
  overflow: hidden;
  
  &:hover {
    opacity: 0.9;
  }
`;

export const ImageFullscreen = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  cursor: pointer;
`;

export const ImageOverlay = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ImageActions = styled.div`
  position: absolute;
  top: ${theme.spacing.sm};
  right: ${theme.spacing.sm};
  display: flex;
  gap: ${theme.spacing.xs};
  opacity: 0;
  transition: opacity ${theme.transitions.normal};
  
  ${ImagePreview}:hover & {
    opacity: 1;
  }
`;

export const ImageActionButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(0, 0, 0, 0.7);
  color: ${theme.colors.text.light};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  transition: all ${theme.transitions.normal};
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
  }
`;

export const ImageInfo = styled.div`
  margin-top: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  background: rgba(0, 0, 0, 0.1);
  border-radius: ${theme.borderRadius.sm};
`;

export const ImageName = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: ${theme.spacing.xs};
`;

export const ImageSize = styled.div`
  font-size: 0.75rem;
  opacity: 0.8;
`;

export const LoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top: 3px solid ${theme.colors.primary};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// File Message Styles
export const FileMessageContainer = styled.div<{ $isOwn: boolean }>`
  max-width: 70%;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.lg};
  background: ${(props) =>
    props.$isOwn
      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      : theme.colors.background.secondary};
  color: ${(props) =>
    props.$isOwn ? theme.colors.text.light : theme.colors.text.primary};
  box-shadow: ${theme.shadows.light};
  position: relative;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    max-width: 85%;
    padding: ${theme.spacing.md};
    border-radius: ${theme.borderRadius.xl};
  }
`;

export const FilePreview = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  background: rgba(0, 0, 0, 0.1);
  border-radius: ${theme.borderRadius.sm};
  margin-bottom: ${theme.spacing.sm};
`;

export const FileIcon = styled.div`
  font-size: 2rem;
  flex-shrink: 0;
`;

export const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const FileName = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: ${theme.spacing.xs};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const FileSize = styled.div`
  font-size: 0.75rem;
  opacity: 0.8;
`;

export const FileActions = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  opacity: 0;
  transition: opacity ${theme.transitions.normal};
  
  ${FilePreview}:hover & {
    opacity: 1;
  }
`;

export const FileActionButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  color: inherit;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  transition: all ${theme.transitions.normal};
  
  &:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`;

export const DownloadButton = styled.button`
  width: 100%;
  background: rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: ${theme.borderRadius.sm};
  padding: ${theme.spacing.sm};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.normal};
  
  &:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`;

// Audio Message Styles
export const AudioMessageContainer = styled.div<{ $isOwn: boolean }>`
  max-width: 70%;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.lg};
  background: ${(props) =>
    props.$isOwn
      ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
      : theme.colors.background.secondary};
  color: ${(props) =>
    props.$isOwn ? theme.colors.text.light : theme.colors.text.primary};
  box-shadow: ${theme.shadows.light};
  position: relative;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    max-width: 85%;
    padding: ${theme.spacing.md};
    border-radius: ${theme.borderRadius.xl};
  }
`;

export const AudioPlayer = styled.div`
  padding: ${theme.spacing.sm};
  background: rgba(0, 0, 0, 0.1);
  border-radius: ${theme.borderRadius.sm};
`;

export const AudioControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.sm};
`;

export const PlayButton = styled.button<{ disabled?: boolean }>`
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(0, 0, 0, 0.2);
  color: inherit;
  border-radius: 50%;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  transition: all ${theme.transitions.normal};
  flex-shrink: 0;
  
  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.3);
  }
`;

export const AudioProgress = styled.div`
  flex: 1;
  cursor: pointer;
`;

export const AudioProgressBar = styled.div`
  height: 4px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
  overflow: hidden;
`;

export const AudioProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  background: currentColor;
  width: ${props => props.$progress}%;
  transition: width ${theme.transitions.normal};
`;

export const AudioTime = styled.div`
  font-size: 0.75rem;
  opacity: 0.8;
  white-space: nowrap;
  flex-shrink: 0;
`;

export const AudioInfo = styled.div`
  margin-bottom: ${theme.spacing.sm};
`;

export const AudioName = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: ${theme.spacing.xs};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const AudioSize = styled.div`
  font-size: 0.75rem;
  opacity: 0.8;
`;

export const AudioActions = styled.div`
  display: flex;
  gap: ${theme.spacing.xs};
  opacity: 0;
  transition: opacity ${theme.transitions.normal};
  
  ${AudioPlayer}:hover & {
    opacity: 1;
  }
`;

export const AudioActionButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(0, 0, 0, 0.1);
  color: inherit;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  transition: all ${theme.transitions.normal};
  
  &:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`;
